import { Button } from '@myog/ui';
import { useState } from 'react';
import { Navigate, useSearchParams } from 'react-router';
import { signIn, useSession } from '../lib/auth-client.js';

/**
 * Page de connexion — la seule route publique du dashboard.
 *
 * Un seul bouton : c'est délibéré. La cible est développeur, elle a un compte
 * GitHub, et chaque champ supplémentaire est un abandon. Pas de mot de passe
 * non plus (`emailAndPassword: false` côté serveur) : pas de formulaire à
 * protéger, pas de hachage à faire vieillir, pas d'email de réinitialisation.
 */

/**
 * Better Auth redirige ici avec `?error=...` quand GitHub refuse. Le message
 * brut du fournisseur ne dit rien d'utile à l'utilisateur — on traduit les
 * cas connus, et on garde une formulation neutre pour le reste.
 */
const OAUTH_ERRORS: Record<string, string> = {
  access_denied: "Vous avez refusé l'accès à votre compte GitHub.",
  invalid_client: "La connexion GitHub n'est pas correctement configurée côté serveur.",
  signup_disabled: 'Les inscriptions sont momentanément fermées.',
};

export function Login() {
  const { data: session, isPending } = useSession();
  const [params] = useSearchParams();
  const [pending, setPending] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const redirectError = params.get('error');
  const message =
    failure ??
    (redirectError === null
      ? null
      : (OAUTH_ERRORS[redirectError] ?? 'La connexion a échoué. Réessayez.'));

  const connect = async (): Promise<void> => {
    setPending(true);
    setFailure(null);

    const origin = window.location.origin;
    const result = await signIn.social({
      provider: 'github',
      callbackURL: `${origin}/`,
      errorCallbackURL: `${origin}/login`,
    });

    // En cas de succès le navigateur part chez GitHub : ce code n'est atteint
    // que si la requête d'initialisation elle-même a échoué.
    if (result.error) {
      setFailure(
        result.error.message ?? "Le service d'authentification est injoignable. Réessayez.",
      );
      setPending(false);
    }
  };

  // Déjà connecté : on ne laisse pas une page de login accessible, elle
  // donnerait l'impression d'une session perdue.
  if (session) {
    return <Navigate to="/" replace />;
  }

  return (
    <main className="grid min-h-screen place-items-center bg-bg px-6">
      <div className="w-full max-w-sm space-y-8">
        <header className="space-y-2 text-center">
          <p className="text-2xl font-bold tracking-tight">
            <span className="text-text">My</span>
            <span className="text-accent">OG</span>
          </p>
          <h1 className="text-sm text-text-muted">
            Connectez-vous pour gérer vos clés API et votre consommation.
          </h1>
        </header>

        <Button
          variant="secondary"
          size="lg"
          className="w-full"
          loading={pending || isPending}
          onClick={() => void connect()}
        >
          <GitHubMark />
          Continuer avec GitHub
        </Button>

        <p aria-live="polite" className="min-h-5 text-center text-sm">
          {message === null ? null : (
            <span role="alert" className="text-danger">
              {message}
            </span>
          )}
        </p>

        <p className="text-center text-xs text-text-subtle">
          En continuant, vous acceptez les{' '}
          <a
            href="https://myog.dev/cgu"
            className="underline decoration-border-strong underline-offset-2 hover:text-text-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          >
            conditions d'utilisation
          </a>{' '}
          et la{' '}
          <a
            href="https://myog.dev/confidentialite"
            className="underline decoration-border-strong underline-offset-2 hover:text-text-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          >
            politique de confidentialité
          </a>
          .
        </p>
      </div>
    </main>
  );
}

function GitHubMark() {
  return (
    <svg viewBox="0 0 16 16" className="h-4 w-4" fill="currentColor" aria-hidden="true">
      <path d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.01 8.01 0 0 0 16 8c0-4.42-3.58-8-8-8z" />
    </svg>
  );
}
